import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, Inject } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { PaginatedRepository } from '../pagination/paginated.repository';
import { REG_HUB_COMMON_CONFIG, RegHubCommonConfig } from '../../reg-hub-common-config';
import { ReusableParty } from '../../interfaces/reusable-parties/reusable-party';
import { ReusablePartyForCreation } from '../../interfaces/reusable-parties/reusable-party-for-creation';
import { ReusablePartyForUpdate } from '../../interfaces/reusable-parties/reusable-party-for-update';
import { ReusablePartyLink } from '../../interfaces/reusable-parties//reusable-party-link';
import { ePartyLinkType } from '../../enums';
import { ReusablePartyLinkForCreation } from '../../interfaces/reusable-parties/reusable-party-link-for-creation';

@Injectable({
    providedIn: 'root'
})
export class ReusablePartyRepositoryService extends PaginatedRepository<ReusableParty> {
    protected override _baseLink: string;

    constructor(
        http: HttpClient,
        messageService: ToastrService,
        @Inject(REG_HUB_COMMON_CONFIG) private config: RegHubCommonConfig) {

        super(http, messageService);

        this._currentParams = new HttpParams()
            .set('pageSize', 10);

        this._baseLinkTemplate = `${config.rootUrl}/Corporations/[0]/ReusableParties`;
        this._baseLink = this._baseLinkTemplate;
    }

    // Lists the corporation's parties, or the user group's when a userGroupID is given
    public populateScopedBaseLink(corporationID: string, userGroupID?: string | null) {
        var urlSlug = `/Corporations/${corporationID}`
        if (userGroupID) {
            urlSlug = `${urlSlug}/UserGroups/${userGroupID}`
        }

        this._baseLink = `${this.config.rootUrl}${urlSlug}/ReusableParties`;
    }

    private generateHeaders = () => {
        return {
            headers: new HttpHeaders({ 'Content-Type': 'application/json' })
        }
    }

    public createReusableParty = (corporationID: string, reusableParty: ReusablePartyForCreation, userGroupID?: string | null) => {
        var urlSlug = `/Corporations/${corporationID}`
        if (userGroupID) {
            urlSlug = `${urlSlug}/UserGroups/${userGroupID}`
        }

        return this.http.post<ReusableParty>(
            `${this.config.rootUrl}${urlSlug}/ReusableParties`,
            reusableParty,
            this.generateHeaders());
    }

    public getReusableParty = (corporationID: string, reusablePartyID: string, userGroupID?: string | null) => {
        var urlSlug = `/Corporations/${corporationID}`
        if (userGroupID) {
            urlSlug = `${urlSlug}/UserGroups/${userGroupID}`
        }

        return this.http.get<ReusableParty>(
            `${this.config.rootUrl}${urlSlug}/ReusableParties/${reusablePartyID}`,
            this.generateHeaders());
    }

    // usableOnly: the parties usable at this level - the user group's own (or the corporation's when the group falls back to it),
    // or only the corporation level parties when no user group is given
    public getReusableParties(corporationID: string, options?: { userGroupID?: string, pageSize?: number, usableOnly?: boolean }) {
        var urlSlug = `/Corporations/${corporationID}`
        var params = new HttpParams().set("PageSize", options?.pageSize ?? 10);

        if (options?.userGroupID) {
            urlSlug = `${urlSlug}/UserGroups/${options?.userGroupID}`
            if (options?.usableOnly) {
                params = params.set('UseCorporationFallback', true);
            }
        } else if (options?.usableOnly) {
            params = params.set('CorporationLevelOnly', true);
        }

        return this.http.get<ReusableParty[]>(
            `${this.config.rootUrl}${urlSlug}/ReusableParties?IsVisible=true`,
            {
                headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
                params: params
            });
    }

    public updateReusableParty = (corporationID: string, reusablePartyID: string, reusableParty: ReusablePartyForUpdate, userGroupID?: string | null) => {
        var urlSlug = `/Corporations/${corporationID}`
        if (userGroupID) {
            urlSlug = `${urlSlug}/UserGroups/${userGroupID}`
        }

        return this.http.put<ReusablePartyForUpdate>(
            `${this.config.rootUrl}${urlSlug}/ReusableParties/${reusablePartyID}`,
            reusableParty,
            this.generateHeaders());
    }

    public deleteReusableParty = (corporationID: string, reusablePartyID: string, userGroupID?: string | null) => {
        var urlSlug = `/Corporations/${corporationID}`
        if (userGroupID) {
            urlSlug = `${urlSlug}/UserGroups/${userGroupID}`
        }

        return this.http.delete(
            `${this.config.rootUrl}${urlSlug}/ReusableParties/${reusablePartyID}`,
            this.generateHeaders());
    }

    public linkPartiesByID(corporationID: string, parentPartyID: string, childPartyID: string) {
        let partyForCreation: ReusablePartyLinkForCreation = {
            partyLinkType: ePartyLinkType.association,
            reusablePartyID: parentPartyID,
            linkedReusablePartyID: childPartyID
        };

        return this.linkParties(corporationID, partyForCreation);
    }

    public linkParties(corporationID: string, partyLinkForCreation: ReusablePartyLinkForCreation) {
        this.populateBaseLink([corporationID]);

        return this.http.post<ReusablePartyLink>(
            `${this._baseLink}/${partyLinkForCreation.reusablePartyID}/LinkedParties`,
            partyLinkForCreation);
    }

    public unlinkParties(corporationID: string, reusablePartyID: string, reusablePartyLinkID: string) {
        this.populateBaseLink([corporationID]);

        return this.http.delete<ReusablePartyLink>(
            `${this._baseLink}/${reusablePartyID}/LinkedParties/${reusablePartyLinkID}`);
    }
}
